import { useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { X, AlertTriangle, Check } from 'lucide-react'
import PhotoUpload from './PhotoUpload'

// ANU-89 — War Crime Report Modal.
// Opens on top of PlayerView / RefereeView. Captures geo silently on
// mount (high accuracy, 5s timeout) — if denied or times out we leave
// geo fields null and continue. Form: incident type, optional accused,
// required description (>=10 chars), optional photo evidence via the
// ANU-88 PhotoUpload component.
//
// On submit: INSERT war_crimes — the AFTER INSERT trigger _notify_war_crime
// fans the alert out to all GMs + IGAs on the event server-side, so a
// client crash mid-submit can't drop the notification.

const INCIDENT_TYPES = [
  { value: 'team_kill',     label: 'Team Kill' },
  { value: 'unsafe_play',   label: 'Unsafe Play' },
  { value: 'hit_dispute',   label: 'Hit Dispute' },
  { value: 'cheating',      label: 'Cheating' },
  { value: 'spawn_camping', label: 'Spawn Camping' },
  { value: 'other',         label: 'Other' },
]

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 },
  modal: { background: '#1a1410', border: '1px solid rgba(255,42,42,.45)', width: '100%', maxWidth: 460, padding: 22, position: 'relative', color: '#e0e8de', fontFamily: "'Inter', system-ui, sans-serif", maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 0 30px rgba(255,42,42,.15)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 12, borderBottom: '1px solid rgba(255,42,42,.25)' },
  title: { fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif", fontSize: 18, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#ff6a3d', margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 },
  label: { color: '#8aa090', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6, display: 'block', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 },
  input: { width: '100%', background: 'rgba(0,0,0,.4)', color: '#e0e8de', border: '1px solid rgba(140,180,130,.25)', padding: '10px 12px', fontFamily: 'inherit', fontSize: 14, outline: 'none' },
  textarea: { width: '100%', background: 'rgba(0,0,0,.4)', color: '#e0e8de', border: '1px solid rgba(140,180,130,.25)', padding: '10px 12px', fontFamily: 'inherit', fontSize: 13, outline: 'none', minHeight: 96, resize: 'vertical' },
  hint: { color: '#5a6a60', fontSize: 11, marginTop: 4 },
  close: { background: 'none', border: 'none', color: '#8aa090', cursor: 'pointer', minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  btnPrimary: { width: '100%', minHeight: 52, marginTop: 16, padding: '12px 16px', background: '#ff2a2a', color: '#fff', border: 'none', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  btnPrimaryDisabled: { background: 'rgba(255,42,42,.25)', color: 'rgba(255,255,255,.5)', cursor: 'not-allowed' },
  dropdown: { position: 'absolute', zIndex: 12, left: 0, right: 0, marginTop: 4, background: '#0a0a0a', border: '1px solid rgba(140,180,130,.3)', maxHeight: 220, overflowY: 'auto' },
  dropdownItem: { width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', color: '#e0e8de', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  errorBox: { background: 'rgba(255,42,42,.12)', border: '1px solid rgba(255,42,42,.4)', padding: 10, color: '#ff6a3d', fontSize: 12, marginTop: 12 },
  successBox: { background: 'rgba(40,160,80,.12)', border: '1px solid rgba(40,160,80,.4)', padding: 14, color: '#9adb6e', fontSize: 13, marginTop: 12 },
}

export default function WarCrimeReportModal({ eventId, factionId, onClose }) {
  const { user } = useAuth()
  const geoRef = useRef({ lat: null, lng: null, accuracy: null, captured: false })

  const [incidentType, setIncidentType] = useState('')
  const [accusedId, setAccusedId] = useState(null)
  const [accusedSearch, setAccusedSearch] = useState('')
  const [accusedLabel, setAccusedLabel] = useState('')
  const [showAccusedList, setShowAccusedList] = useState(false)
  const [description, setDescription] = useState('')
  const [photo, setPhoto] = useState({ url: null, path: null })
  const [players, setPlayers] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [submittedRef, setSubmittedRef] = useState(null)

  // Geo capture on open — silent, no permission UI prompt of our own.
  // The browser's own dialog handles the consent surface. If denied or
  // timeout, geo stays null and we proceed normally.
  useEffect(() => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        geoRef.current = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: Math.round(pos.coords.accuracy || 0),
          captured: true,
        }
      },
      () => { /* denied / timeout / unavailable — stay null */ },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
    )
  }, [])

  // Load event roster for the accused dropdown.
  useEffect(() => {
    if (!eventId) return
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('event_players')
        .select('user_id, faction_id, users:user_id(id, callsign, display_name)')
        .eq('event_id', eventId)
      if (!cancelled) setPlayers(data || [])
    })()
    return () => { cancelled = true }
  }, [eventId])

  const filteredPlayers = (() => {
    const q = accusedSearch.toLowerCase()
    return players
      .filter(p => p.user_id !== user?.id)
      .filter(p => {
        if (!q) return true
        const cs = (p.users?.callsign || '').toLowerCase()
        const dn = (p.users?.display_name || '').toLowerCase()
        return cs.includes(q) || dn.includes(q)
      })
      .slice(0, 30)
  })()

  const canSubmit = !!incidentType && description.trim().length >= 10 && !submitting

  const handleSubmit = async () => {
    if (!canSubmit || !user) return
    setSubmitting(true)
    setError('')
    const payload = {
      event_id: eventId,
      reporter_id: user.id,
      accused_id: accusedId || null,
      incident_type: incidentType,
      description: description.trim(),
      photo_url: photo.url,
      photo_storage_path: photo.path,
      geo_lat: geoRef.current.lat,
      geo_lng: geoRef.current.lng,
      geo_accuracy_m: geoRef.current.accuracy,
    }
    const { data, error: err } = await supabase
      .from('war_crimes')
      .insert(payload)
      .select('id')
      .limit(1)
    if (err) {
      setError(err.message || 'Failed to file report.')
      setSubmitting(false)
      return
    }
    const ref = `#${(data?.[0]?.id || '').slice(0, 8).toUpperCase()}`
    setSubmittedRef(ref)
    setSubmitting(false)
  }

  // Success state — leaves a confirmation, no auto-close so the reporter
  // can copy the reference number if they want to follow up.
  if (submittedRef) {
    return (
      <div style={S.overlay}>
        <div style={S.modal}>
          <div style={S.header}>
            <h2 style={{ ...S.title, color: '#9adb6e' }}><Check size={18} /> REPORT FILED</h2>
            <button onClick={onClose} style={S.close}><X size={20} /></button>
          </div>
          <div style={S.successBox}>
            <p>Report submitted. Reference: <strong style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em' }}>{submittedRef}</strong></p>
            <p style={{ ...S.hint, color: '#9adb6e', marginTop: 8 }}>GMs and referees have been notified. Stay alert and follow command directives.</p>
          </div>
          <button onClick={onClose} style={{ ...S.btnPrimary, background: '#3a6a4a', marginTop: 18 }}>CLOSE</button>
        </div>
      </div>
    )
  }

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.header}>
          <h2 style={S.title}><AlertTriangle size={18} /> FILE WAR CRIME</h2>
          <button onClick={onClose} style={S.close}><X size={20} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={S.label}>Incident Type <span style={{ color: '#ff6a3d' }}>*</span></label>
            <select value={incidentType} onChange={(e) => setIncidentType(e.target.value)} style={S.input}>
              <option value="">— Select —</option>
              {INCIDENT_TYPES.map(t => (
                <option key={t.value} value={t.value} style={{ background: '#1a1410' }}>{t.label}</option>
              ))}
            </select>
          </div>

          <div style={{ position: 'relative' }}>
            <label style={S.label}>Accused <span style={{ color: '#5a6a60' }}>(leave blank if unknown)</span></label>
            <input
              type="text"
              value={accusedLabel || accusedSearch}
              onChange={(e) => { setAccusedSearch(e.target.value); setAccusedLabel(''); setAccusedId(null); setShowAccusedList(true) }}
              onFocus={() => setShowAccusedList(true)}
              onBlur={() => setTimeout(() => setShowAccusedList(false), 120)}
              placeholder="Search callsign or display name..."
              style={S.input}
            />
            {showAccusedList && accusedSearch.length > 0 && filteredPlayers.length > 0 && (
              <div style={S.dropdown}>
                {filteredPlayers.map(p => (
                  <button key={p.user_id} type="button"
                    onMouseDown={() => {
                      setAccusedId(p.user_id)
                      setAccusedLabel(p.users?.callsign || p.users?.display_name || 'Unknown')
                      setAccusedSearch('')
                      setShowAccusedList(false)
                    }}
                    style={S.dropdownItem}
                  >
                    <span style={{ color: '#e0e8de' }}>{p.users?.callsign || '—'}</span>
                    <span style={{ color: '#8aa090', marginLeft: 8 }}>{p.users?.display_name}</span>
                  </button>
                ))}
              </div>
            )}
            <p style={S.hint}>Optional — file the report anyway if you don't know who.</p>
          </div>

          <div>
            <label style={S.label}>Description <span style={{ color: '#ff6a3d' }}>*</span></label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened? Be specific — time, location, what you saw..."
              maxLength={2000}
              style={S.textarea}
            />
            <p style={S.hint}>{description.trim().length} / 2000 chars (min 10)</p>
          </div>

          <div>
            <label style={S.label}>Photo Evidence <span style={{ color: '#5a6a60' }}>(optional)</span></label>
            <PhotoUpload
              folder={`${eventId}/war-crimes/${user?.id}`}
              label="Add Photo Evidence"
              onUpload={(url, path) => setPhoto({ url, path })}
            />
          </div>

          {error && <div style={S.errorBox}>{error}</div>}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{ ...S.btnPrimary, ...(canSubmit ? {} : S.btnPrimaryDisabled) }}
          >
            <AlertTriangle size={14} />
            {submitting ? 'FILING...' : 'FILE REPORT'}
          </button>
          <p style={S.hint}>Filing notifies GMs and referees instantly. Location is captured silently if your browser allows it.</p>
        </div>
      </div>
    </div>
  )
}
